<script setup lang="ts">
import { onMounted } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import CustomerCodeAutocompleteField from '@/components/fields/CustomerCodeAutocompleteField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import AddressBookPanel from '@/components/panels/AddressBookPanel.vue';
import AddressBookTable from '@/components/tables/AddressBookTable.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import useAddressBookViewData from '@/composables/data/useAddressBookViewData';
import { useAuthStore } from '@/stores/useAuthStore';

const authStore = useAuthStore();

const {
  books,
  isFetchingBooks,
  booksError,
  customBooks,
  isFetchingCustomBooks,
  customBooksError,
  isReordering,
  fetchOwnBooks,
  searchCustomerCode,
  searchUsername,
  hasCustomSearch,
  handleCustomerCodeUpdate,
  handleUsernameInput,
  handleSearchClick,
  openAddAddressDialog,
  openEditAddressDialog,
  openDeleteAddressDialog,
  handleReorder,
  handleSetDefault,
} = useAddressBookViewData();

onMounted(() => {
  void fetchOwnBooks();
});
</script>

<template>
  <PageContentWrapper>
    <div
      :class="$style.userAddressBookProfileView"
      data-testid="userAddressBookProfileView"
    >
      <template v-if="authStore.isStaff">
        <FormWrapper :class="$style.formWrapper">
          <h2 class="g-h2">
            Search another address book by customer code or username
          </h2>
          <div :class="$style.staffSearchRow">
            <CustomerCodeAutocompleteField
              :model-value="searchCustomerCode"
              hide-details
              :class="$style.staffSearchField"
              @update:model-value="handleCustomerCodeUpdate"
            />
            <KTextField
              :model-value="searchUsername"
              label="Username"
              hide-details
              :class="$style.staffSearchField"
              @update:model-value="handleUsernameInput"
            />
            <KBtn :class="$style.searchBtn" @click="handleSearchClick">
              Search
            </KBtn>
          </div>
        </FormWrapper>

        <div v-if="isFetchingCustomBooks" :class="$style.loaderContainer">
          <SimpleLoader :size="48" :width="5" />
        </div>
        <TableEmptyText v-else-if="customBooksError">
          Could not load the address book. Please try again.
        </TableEmptyText>
        <TableEmptyText v-else-if="hasCustomSearch && !customBooks.length">
          No address books found
        </TableEmptyText>
        <template v-else>
          <AddressBookPanel
            v-for="book in customBooks"
            :key="book.id"
            :title="book.name"
          >
            <template #actions>
              <KBtn
                variant="outlined"
                prepend-icon="mdi-plus"
                @click="openAddAddressDialog(book)"
              >
                Add address
              </KBtn>
            </template>
            <AddressBookTable
              :addresses="book.addresses"
              :show-default-toggle="!!book.customer_code"
              :reorder-disabled="isReordering"
              @edit-click="(address) => openEditAddressDialog(book, address)"
              @delete-click="
                (address) => openDeleteAddressDialog(book, address)
              "
              @reorder="(id, newIndex) => handleReorder(book, id, newIndex)"
              @set-default-click="(address) => handleSetDefault(book, address)"
            />
          </AddressBookPanel>
        </template>
      </template>

      <div v-if="isFetchingBooks" :class="$style.loaderContainer">
        <SimpleLoader :size="48" :width="5" />
      </div>
      <TableEmptyText v-else-if="booksError">
        Could not load your address book. Please refresh the page.
      </TableEmptyText>
      <TableEmptyText v-else-if="!books.length">
        No addresses yet
      </TableEmptyText>
      <template v-else>
        <AddressBookPanel
          v-for="book in books"
          :key="book.id"
          :title="book.name"
        >
          <template #actions>
            <KBtn
              variant="outlined"
              prepend-icon="mdi-plus"
              @click="openAddAddressDialog(book)"
            >
              Add address
            </KBtn>
          </template>
          <AddressBookTable
            :addresses="book.addresses"
            :show-default-toggle="!!book.customer_code"
            :reorder-disabled="isReordering"
            @edit-click="(address) => openEditAddressDialog(book, address)"
            @delete-click="(address) => openDeleteAddressDialog(book, address)"
            @reorder="(id, newIndex) => handleReorder(book, id, newIndex)"
            @set-default-click="(address) => handleSetDefault(book, address)"
          />
        </AddressBookPanel>
      </template>
    </div>
  </PageContentWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
.userAddressBookProfileView {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1200px;
  margin-top: 2rem;
}
  .formWrapper {
    max-width: max-content;
    padding: 0.5rem 1.5rem 1.5rem 1.5rem;
  }
  .staffSearchRow {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
    .staffSearchField {
      max-width: 320px;
    }
    .searchBtn {
      flex-shrink: 0;
    }
  .loaderContainer {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
  }
</style>
